'use client';
import { PATH } from '@/constants';
import { NAV_HEADER_MENUS } from '@/constants/nav';
import { useAuth } from '@/contexts';
import clsx from 'clsx';
import { getImageUrl } from '@/lib/cdn';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { FC, useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import MobileMenu from './MobileMenu';
import ProductsDropdown from './ProductsDropdown';

type IHeaderProps = object;

const HEADER_LINK_CLASS =
  'relative text-base text-black hover:text-[#0848E7] transition-colors duration-200';

const HeaderNavLink: FC<{
  href: string;
  title: string;
  pathname: string;
}> = ({ href, title, pathname }) => {
  const isActive = href === PATH.HOME ? pathname === PATH.HOME : pathname === href;

  return (
    <a
      className={HEADER_LINK_CLASS}
      href={href}
      onClick={(e) => {
        if (href.startsWith('/#') && pathname === PATH.HOME) {
          e.preventDefault();
          const targetId = href.substring(2);
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }}
    >
      {title}
      {isActive && (
        <span className='inline-block absolute -bottom-3 right-0 left-0 h-[2.5px] w-full bg-[#0848E7] rounded-full' />
      )}
    </a>
  );
};

const Header: FC<IHeaderProps> = () => {
  const posthog = usePostHog();
  const [stickyMenu, setStickyMenu] = useState(false);
  const { isAuthenticated } = useAuth();

  const pathname = usePathname() ?? '';

  useEffect(() => {
    const onScroll = () => setStickyMenu(window.pageYOffset > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navMenusAfterProducts = NAV_HEADER_MENUS.filter((item) => item.title !== 'Home');

  return (
    <>
      <header
        className={clsx(
          'header fixed left-0 top-0 w-full z-99999 h-[92px] flex items-center justify-center bg-white transition-shadow duration-100',
          stickyMenu
            ? 'shadow-[0px_1px_1px_rgba(0,0,0,0.1),0px_1px_0.5px_rgba(0,0,0,0.06),0px_0px_0.5px_rgba(0,0,0,0.2)]'
            : 'shadow-[0px_1px_1px_rgba(0,0,0,0.06)]',
        )}
      >
        <div className='container-fluid px-4 md:px-7.5 xl:px-25'>
          <div className='header-wrapper flex items-center justify-between gap-4'>
            <div className='header-left flex items-center gap-6 min-w-0'>
              <Link href='/'>
                <Image
                  className='w-36 xl:w-44'
                  src={getImageUrl('logo/mb_logo.png')}
                  alt='marketbetter AI logo'
                  width={144}
                  height={48}
                  unoptimized
                  priority
                />
              </Link>
              <nav className='header-nav hidden min-[890px]:block'>
                <ul className='flex items-center gap-8 xl:gap-10'>
                  <li>
                    <HeaderNavLink href={PATH.HOME} title='Home' pathname={pathname} />
                  </li>
                  <li className='cursor-pointer'>
                    <ProductsDropdown />
                  </li>
                  {navMenusAfterProducts.map((item, index) => (
                    <li className='cursor-pointer' key={index}>
                      {!!item?.subMenus?.length ? (
                        <Popover>
                          <PopoverTrigger>
                            <p className={HEADER_LINK_CLASS}>{item.title}</p>
                          </PopoverTrigger>
                          <PopoverContent className='z-[99999] w-[200px]'>
                            <ul className='flex flex-col gap-4'>
                              {item.subMenus.map(
                                (subItem: { title: string; href: string }, subIndex) => (
                                  <li
                                    className='text-dark-5 hover:text-primary text-sm transition-all duration-300'
                                    key={subIndex}
                                  >
                                    {subItem.href.startsWith('http') ? (
                                      <a
                                        href={subItem.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                      >
                                        {subItem.title}
                                      </a>
                                    ) : (
                                      <Link href={subItem.href}>{subItem.title}</Link>
                                    )}
                                  </li>
                                ),
                              )}
                            </ul>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <HeaderNavLink href={item.href} title={item.title} pathname={pathname} />
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            <div className='header-right hidden min-[890px]:flex gap-[18px] items-center shrink-0'>
              {isAuthenticated ? (
                <Button
                  className='!h-[50px] !min-w-[170px] !px-[29px] !text-base'
                  href={PATH.DASHBOARD}
                  linearOnHover
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  {pathname !== PATH.LOGIN && (
                    <Link
                      href={PATH.LOGIN}
                      className='text-sm font-medium text-slate-700 transition-colors hover:text-[#0848E7]'
                      onClick={() => posthog?.capture('nav_login_clicked')}
                    >
                      Login
                    </Link>
                  )}
                  <Link
                    href='/book-demo'
                    className='text-sm font-medium text-[#0848E7] transition-colors hover:text-[#073dcc]'
                    onClick={() => posthog?.capture('nav_book_demo_clicked', { source: 'header' })}
                  >
                    Book a call
                  </Link>
                </>
              )}
            </div>
            <MobileMenu className='block min-[890px]:hidden' />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
